//Dependencies
import React, {
  useCallback,
  useMemo,
  useRef,
  useState,
  useLayoutEffect,
  useEffect,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useFilterKeyboardShortcuts } from '../../../../hooks/useFilterKeyboardShortcuts';
import { useTableCurrentPage } from '../../../../hooks/useTableCurrentPage';
import {
  createColumnHelper,
  ColumnFiltersState,
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  flexRender,
  PaginationState,
  getPaginationRowModel,
  VisibilityState,
  Row,
  FilterFnOption,
  RowSelectionState,
} from '@tanstack/react-table';
import { format, formatISO, parseISO } from 'date-fns';
import isEqual from 'lodash.isequal';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

// Double Tap Support
import { DoubleTapRow } from '../components/DoubleTapRow';

// Styled Components
import {
  TrStyled,
  TextCellStyled,
  TableContainerStyled,
  ShowFiltersButton,
  SubContainerHead,
  ArrowBottomStyled,
  ContainerLoader,
  ContainerStyled,
  FiltersWithLoadingStyled,
} from './styles';
import {
  Th,
  Tbody,
  Td,
  Title,
  ContainerButtons,
  Table,
  SubContainerFilters,
  TextHeader,
  FigureGreen,
  FigureRed,
  GreenArrowStyled,
  RedArrowStyled,
  InputCheckContainer,
  CheckBoxStyled,
  InputCalendarStyled,
  TitleInvisible,
  SearchableDropdownStyled,
  MultiSelectDropdownStyled,
} from '../components/MainStyledComponents';
import { SortableHeader } from '../components/SortableHeader';

// Components
import DropDownInput from '../../../inputs/DropDown/DropDownInput';
import { ImperativeRefMultiSelect } from '../../../inputs/DropDown/MultiSelectDropdown/types';
import IncludeInactiveToggle from '../../../inputs/IncludeInactiveToggle';
import { PaginationButtons } from '../components/PaginationButtons';
import { SelectionBanner } from '../components/SelectionBanner';
import InputText from '../../../inputs/InputText';
import InputExpedient from '../../../inputs/InputExpedient';
import InputIndex from '../../../inputs/InputIndex';
import InputPK from '../../../inputs/InputPK';
import { DropDownVisibilityColumn } from '../components/DropDownVisibilityColumn';
import { createFlagColumn, exportIgnoreProps } from '../components/flagColumn';
import { useFlagScopeTexts } from '../../../../hooks/useFlagScopeTexts';
import { ButtonIconBackground } from '../../../inputs/buttons/ButtonIconBackground';
import { SpecialTag } from '../../SpecialTag';
import { variant, size } from '../../SpecialTag/types';

// Types
import { tableProps, dataTable, option } from './types';
import {
  onChangeValue,
  Options,
} from '../../../inputs/DropDown/DropDownList/types';
import { ImperativeRefSearchableDropdown } from '../../../inputs/SearchableDropdown/types';
import { ImperativeRefInput } from '../../../inputs/DropDown/DropDownInput/types';
import {
  ImperativeRefCalendarInput,
  onChangeInputType,
} from '../../../inputs/Calendar/types';
import {
  ImperativeRefInputExpedient,
  onChangeInputExpedientTypes,
} from '../../../inputs/InputExpedient/types';
import {
  ImperativeRefInputIndex,
  onChangeInputIndexTypes,
} from '../../../inputs/InputIndex/types';
import {
  ImperativeRefInputPK,
  onChangeInputPKTypes,
} from '../../../inputs/InputPK/types';
import {
  columnID,
  columnNames,
  getColumnId,
  getColumnNames,
} from '../components/types';
import { Order } from '../../../../types/orders';
import { CommunicationSendStatus } from '../../../../types/communication';
import { takeBase, tableIds } from '../types';
import { useServerPageSize } from '../utils/useServerPageSize';
import { useMirroredCursor } from '../utils/useMirroredCursor';
import { filterAndSortMultimediaUsers } from '../../../../utils/multimediaUserFilters';

// Mocks
import { mocksTable } from './mocks';
import { roidFilterFunction, dateFilterFunction } from './utils';
import { formatRoidForDisplay } from '../utils/roidFormatter';

// Media
import {
  useIsLandscapeTablet,
  useIsPortraitTablet,
} from '../../../../hooks/useMediaBreakPoints';

// Redux
import {
  getCursor,
  getIsLoadingData,
} from '../../../../state/selectors/ui/tables';
import { client } from '../../../../gql/client';
import { GET_CLASSIFICATION_BY_ID_OR_ROID } from '../../../../gql/queries/orders/predefinedData';
import { setCursor } from '../../../../state/actions/ui/cursorTables';

// Utils
import { generateRoidPadding, getStateOptionsByRoleAndEntityType } from '../../../../utils/order';
import { TABLE_ENTITY_TYPES, listingFiltersByRole } from '../types';
import { GET_ROADS_BY_IDS } from '../../../../gql/queries/geoPositions/roads';
import { Loader } from '../../../inputs/Loader';
import { Role } from '../../../../types/auth';
import { useFeatureFlag } from '../../../../hooks/useFeatureFlag';

// Columnas ordenables server-side → campo del backend (whitelist Communication)
const SORTABLE_COLUMNS = {
  state: 'state',
  registerInitDate: 'registerInitDate',
  creationDate: 'creationDate',
  pkInit: 'pkInitKm',
  pkEnd: 'pkEndKm',
  sendStatus: 'sendStatus',
} as const;
const DEFAULT_SORT_FIELD = 'registerInitDate';

/**
 * Por defecto la bandera marca la Order de la fila: la fila de comunicación ya
 * lleva su id (`selectCommunicationOrders` → `order.id`). Fuera del componente
 * para que la referencia sea estable y no rehaga las columnas en cada render.
 */
const defaultGetFlagOrderId = (row: dataTable): string | undefined =>
  row.id || undefined;

export const CheckBoxArrowTable = ({
  idCss,
  titleText,
  data,
  setRowState,
  onReFetchData,
  onReFetchPagination,
  rowState,
  tableId,
  getDoubleTapNavigationPath,
  selectAllMode = false,
  excludedIds,
  onSelectAll,
  onClearSelection,
  rowSelection: propsRowSelection,
  onRowSelectionChange: propsOnRowSelectionChange,
  selectedCount: propsSelectedCount,
  // Fuera de `props`: no son atributos del <table>.
  showFlagColumn = false,
  showOnlyFlaggedFilter = false,
  getFlagOrderId = defaultGetFlagOrderId,
  ...props
}: tableProps): JSX.Element => {
  // Controlled mode: parent provides rowSelection via usePersistentSelection hook
  const isControlled = propsRowSelection !== undefined;
  const { t } = useTranslation(['tables', 'common', 'forms']);
  const flagScopeTexts = useFlagScopeTexts();
  const translatedColumnNames = useMemo(() => getColumnNames(t), [t]);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const globalCursor = useSelector(getCursor(tableId), isEqual);
  const isLoadingData = useSelector(getIsLoadingData(tableId), isEqual);
  const dataAll = data?.ordersWithDataCommunication
    ? data?.ordersWithDataCommunication
    : mocksTable;
  const roleActive = data?.roleActive;
  const inputListGroupRef = useRef<ImperativeRefInput>(null);
  const inputListConRef = useRef<ImperativeRefInput>(null);
  const inputDateRef = useRef<ImperativeRefCalendarInput>(null);
  const inputExpedientRef = useRef<ImperativeRefInputExpedient>(null);
  const inputIndexRef = useRef<ImperativeRefInputIndex>(null);
  const inputPKInitRef = useRef<ImperativeRefInputPK>(null);
  const inputPKEndRef = useRef<ImperativeRefInputPK>(null);
  const inputRoadRef = useRef<ImperativeRefInput>(null);
  const inputListClassificationRef = useRef<ImperativeRefSearchableDropdown>(null);
  const inputStateRef = useRef<ImperativeRefMultiSelect>(null);
  const inputUserRef = useRef<ImperativeRefMultiSelect>(null);
  const inputListPredefinedRef = useRef<ImperativeRefSearchableDropdown>(null);
  const hasAutoSelectedConcession = useRef<boolean>(false);

  const showPredefinedFilter = useFeatureFlag('filter_show_predefined', true);

  const [showFilters, setShowFilters] = useState(false);
  const [internalRowSelection, setInternalRowSelection] = useState<RowSelectionState>({});
  const rowSelection = isControlled ? propsRowSelection! : internalRowSelection;
  const setRowSelection = isControlled ? propsOnRowSelectionChange! : setInternalRowSelection;
  const [expedientDefault, setExpedientDefault] = useState<number | undefined>(
    undefined
  );
  const [indexDefault, setIndexDefault] = useState<number | undefined>(
    undefined
  );
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  // C-11.7: persistido en Redux para mantener coherencia paginador↔cursor
  // al navegar a un detail y volver. Ver hooks/useTableCurrentPage.
  const [currentServerPage, setCurrentServerPage] = useTableCurrentPage(
    tableId as string,
  );
  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: takeBase,
  });

  const initialRoadOptions = useMemo(
    () =>
      data?.filtersData?.data?.roads?.map(
        (road: { name: string; id: string }) => ({
          label: road.name,
          value: road.id,
        })
      ),
    [data?.filtersData?.data?.roads]
  );

  const [roadOptions, setRoadOptions] = useState(initialRoadOptions);

  // El reflejo arranca YA con el cursor de Redux, igual que en el resto de
  // tablas. Antes arrancaba con todos los campos a `undefined` y el efecto lo
  // rellenaba tras el primer pintado: los filtros guardados salían en blanco
  // durante un fotograma.
  const [selectorCursor, setSelectorCursor] = useMirroredCursor<{
    description: string | undefined; // filtro por descripción (texto libre)
    take: number;
    cursor: string;
    indexType: number | undefined;
    indexExpedition: number | undefined;
    roadId: string | undefined;
    interlocutorGroupId: string | undefined;
    concessionId: string | undefined;
    fromDate: string | undefined;
    toDate: string | undefined;
    classification: string | undefined;
    predefinedOrderId: string | undefined;
    state: string | undefined; // legacy single (kept for back-compat with other tables sharing this cursor shape)
    states: string[] | undefined; // multi-select (Plan 2 Tarea: filtro estado multi-select Communications)
    userIds: string[] | undefined; // multi-select creador (Plan 2 Tarea 16)
    includeInactive: boolean | undefined; // toggle "Incluir inactivos en Creador" (Plan Task 9)
    pkInitKm: number | undefined;
    pkInitMeter: number | undefined;
    pkEndKm: number | undefined;
    pkEndMeter: number | undefined;
    sortField: string | undefined;
    orderBy: 'asc' | 'desc' | undefined;
    onlyFlagged: true | undefined; // «Solo marcadas»: true o undefined, nunca false
  }>(globalCursor);
  const [selectorData, setSelectorData] = useState<{
    description: string | undefined; // filtro por descripción (texto libre)
    indexType: number | undefined;
    indexExpedition: number | undefined;
    roadId: string | undefined;
    interlocutorGroupId: string | undefined;
    concessionId: string | undefined;
    fromDate: string | undefined;
    toDate: string | undefined;
    classification: string | undefined;
    predefinedOrderId: string | undefined;
    state: string | undefined; // legacy single (kept for back-compat)
    states: string[] | undefined; // multi-select
    userIds: string[] | undefined; // multi-select creador (Plan 2 Tarea 16)
    includeInactive: boolean | undefined; // toggle "Incluir inactivos en Creador" (Plan Task 9)
    pkInitKm: number | undefined;
    pkInitMeter: number | undefined;
    pkEndKm: number | undefined;
    pkEndMeter: number | undefined;
    // Este estado arranca con el cursor de Redux (también con `onlyFlagged`) y
    // `useServerPageSize` lo mezcla sobre el cursor al cambiar el tamaño de
    // página: hay que mantenerlo al día o un valor viejo volvería a activar el
    // filtro.
    onlyFlagged: true | undefined;
  }>(globalCursor);

  // Tarea #11 — page-size selector. Activo solo en el listado principal de
  // Communications historical. N+1 audit: ~5 queries/fila (sin nested) → no
  // capamos por ahora ([25, 50, 100] disponibles).
  const { takePagination, paginationProps } = useServerPageSize({
    tableId,
    enabled: tableId === tableIds.SEARCH_AND_CREATE_HISTORICAL_COMMUNICATIONS,
    selectorCursor,
    liveFilters: selectorData as unknown as Record<string, unknown>,
    setCurrentServerPage,
    onReFetchData,
  });

  const isLandscapeTablet = useIsLandscapeTablet();
  const isPortraitTablet = useIsPortraitTablet();

  const [sizeTag, setSizeTag] = useState<size>(
    isPortraitTablet ? 'circle' : 'small'
  );

  useLayoutEffect(() => {
    setSizeTag(isPortraitTablet ? 'circle' : 'small');
  }, [isPortraitTablet]);

  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});

  const handleClick = useCallback(() => {
    setShowFilters(!showFilters);
  }, [showFilters]);

  const callbackSetRoid = useCallback(async () => {
    let classification;
    if (selectorCursor?.classification) {
      const classificationRaw = await client.query({
        query: GET_CLASSIFICATION_BY_ID_OR_ROID,
        variables: {
          id: selectorCursor?.classification,
        },
      });
      classification =
        classificationRaw?.data?.classification?.classificationCode;
    }
    // Actualizar valores por defecto desde el cursor
    setExpedientDefault(
      selectorCursor?.indexExpedition !== undefined &&
        selectorCursor?.indexExpedition >= 0
        ? selectorCursor.indexExpedition
        : undefined
    );
    setIndexDefault(
      selectorCursor?.indexType !== undefined && selectorCursor?.indexType >= 0
        ? selectorCursor.indexType
        : undefined
    );
  }, [selectorCursor]);

  useEffect(() => {
    callbackSetRoid();
  }, [selectorCursor]);

  useEffect(() => {
    isLandscapeTablet ? setShowFilters(false) : setShowFilters(true);
  }, [isLandscapeTablet]);

  // NOTE: Default state filter removed from CheckBoxArrowTable
  // This component is used by Communications which should NOT have default state filter.
  // For tables that need default state filter (Generic WorkOrders), see InputRadioTagTable
  // which uses the centralized config from entityStateFiltering.ts
  // @see BUG #9 fix

  useEffect(() => {
    setColumnVisibility(prev => ({
      ...prev,
      predefinedOrder: showPredefinedFilter,
    }));
  }, [showPredefinedFilter]);

  useEffect(() => {
    const concessions = data.filtersData?.data?.concessions;
    setColumnVisibility(prev => ({
      ...prev,
      concession: !concessions || concessions.length > 1,
    }));
  }, [data.filtersData?.data?.concessions]);

  const columnHelper = createColumnHelper<dataTable>();

  // Ordenación server-side: campo activo y dirección derivados del cursor Redux
  const activeSortField = selectorCursor?.sortField ?? DEFAULT_SORT_FIELD;
  const activeSortColumnId =
    (Object.keys(SORTABLE_COLUMNS) as (keyof typeof SORTABLE_COLUMNS)[]).find(
      (k) => SORTABLE_COLUMNS[k] === activeSortField,
    ) ?? 'registerInitDate';
  const sortDirection: 'asc' | 'desc' =
    selectorCursor?.orderBy === 'asc' ? 'asc' : 'desc';

  const onSortColumn = useCallback(
    (sortField: string) => {
      const isActive = activeSortField === sortField;
      const nextOrderBy: 'asc' | 'desc' =
        isActive && sortDirection === 'desc' ? 'asc' : 'desc';
      const isDefault =
        sortField === DEFAULT_SORT_FIELD && nextOrderBy === 'desc';
      const dataCursor = {
        sortField: isDefault ? undefined : sortField,
        orderBy: nextOrderBy,
        cursor: '',
      };
      dispatch(setCursor(tableId as string, dataCursor));
      setCurrentServerPage(1);
      onReFetchData?.();
    },
    [activeSortField, sortDirection, dispatch, tableId, setCurrentServerPage, onReFetchData],
  );

  const sortableHeader = useCallback(
    (columnId: keyof typeof SORTABLE_COLUMNS) => () => (
      <SortableHeader
        label={translatedColumnNames[columnId as keyof typeof translatedColumnNames]?.label ?? columnId}
        active={activeSortColumnId === columnId}
        direction={sortDirection}
        onToggle={() => onSortColumn(SORTABLE_COLUMNS[columnId])}
      />
    ),
    [translatedColumnNames, activeSortColumnId, sortDirection, onSortColumn],
  );

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: 'select',
        header: ({ table }) => (
          <InputCheckContainer>
            <CheckBoxStyled
              name={'select'}
              {...{
                checked: table.getIsAllRowsSelected(),
                indeterminate: table.getIsSomeRowsSelected(),
                onChange: table.getToggleAllRowsSelectedHandler(),
              }}
            />
          </InputCheckContainer>
        ),
        cell: ({ row }) => (
          <InputCheckContainer
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
          >
            <CheckBoxStyled
              {...{
                checked: row.getIsSelected(),
                disabled: !row.getCanSelect(),
                indeterminate: row.getIsSomeSelected(),
                onChange: row.getToggleSelectedHandler(),
              }}
            />
          </InputCheckContainer>
        ),
      }),
      // Banderitas (spec 2026-10-22 § 8.2): justo detrás de la selección y
      // solo si la pantalla la pide.
      ...(showFlagColumn
        ? [
            createFlagColumn<dataTable>(
              getFlagOrderId),
          ]
        : []),
      columnHelper.accessor(getColumnId(translatedColumnNames.state.id), {
        header: sortableHeader('state'),
        cell: (info) => (
          <SpecialTag sizeTag={sizeTag} variant={info.getValue() as variant} />
        ),
      }),
      columnHelper.accessor(getColumnId(translatedColumnNames.roid.id), {
        header: () => (
          <TextHeader size={'XS'} colorText={'greyDark'}>
            {translatedColumnNames.roid.label}
          </TextHeader>
        ),
        cell: (info) => (
          <TextCellStyled size={'S'} colorText={'black'}>
            {formatRoidForDisplay(info.getValue())}
          </TextCellStyled>
        ),
        filterFn: 'roidFilter' as FilterFnOption<Order>,
      }),
      columnHelper.accessor(
        getColumnId(translatedColumnNames.registerInitDate.id),
        {
          header: sortableHeader('registerInitDate'),
          cell: (info) => (
            <TextCellStyled size={'S'} colorText={'black'}>
              {format(
                new Date(info.getValue() as string),
                'dd/MM/yyyy - HH:mm'
              )}
            </TextCellStyled>
          ),
          /** Add the type of filter that we are going to use and we pass it as a string. The filter types are:
           * includesString, includesStringSensitive, equalsString, equalsStringSensitive, arrIncludes, arrIncludesAll,
           * arrIncludesSome, equals, weakEquals or inNumberRange */
          filterFn: 'dateFilter' as FilterFnOption<dataTable>,
        }
      ),
      columnHelper.accessor(getColumnId(translatedColumnNames.groupInter.id), {
        header: () => (
          <TextHeader size={'XS'} colorText={'greyDark'}>
            {translatedColumnNames.groupInter.label}
          </TextHeader>
        ),
        cell: (info) => (
          <TextCellStyled size={'S'} colorText={'black'}>
            {info.getValue() as string}
          </TextCellStyled>
        ),
      }),
      columnHelper.accessor(getColumnId(translatedColumnNames.media.id), {
        header: () => (
          <TextHeader size={'XS'} colorText={'greyDark'}>
            {translatedColumnNames.media.label}
          </TextHeader>
        ),
        cell: (info) => (
          <TextCellStyled size={'S'} colorText={'black'}>
            {info.getValue() as string}
          </TextCellStyled>
        ),
      }),
      columnHelper.accessor(getColumnId(translatedColumnNames.sendStatus.id), {
        header: sortableHeader('sendStatus'),
        cell: (info) =>
          info.getValue() === CommunicationSendStatus.SENT ? (
            <FigureGreen>
              <TitleInvisible>{info.getValue() as string}</TitleInvisible>
              <GreenArrowStyled />
            </FigureGreen>
          ) : (
            <FigureRed>
              <TitleInvisible>{info.getValue() as string}</TitleInvisible>
              <RedArrowStyled />
            </FigureRed>
          ),
      }),
      columnHelper.accessor(getColumnId(translatedColumnNames.predefinedOrder.id), {
        header: () => (
          <TextHeader size={'XS'} colorText={'greyDark'}>
            {translatedColumnNames.predefinedOrder.label}
          </TextHeader>
        ),
        cell: (info) => (
          <TextCellStyled size={'S'} colorText={'black'}>
            {info.getValue() as string}
          </TextCellStyled>
        ),
      }),
      columnHelper.accessor(getColumnId(translatedColumnNames.concession.id), {
        header: () => (
          <TextHeader size={'XS'} colorText={'greyDark'}>
            {translatedColumnNames.concession.label}
          </TextHeader>
        ),
        cell: (info) => (
          <TextCellStyled size={'S'} colorText={'black'}>
            {info.getValue() as string}
          </TextCellStyled>
        ),
      }),
      columnHelper.accessor(getColumnId(translatedColumnNames.road.id), {
        header: () => (
          <TextHeader size={'XS'} colorText={'greyDark'}>
            {translatedColumnNames.road.label}
          </TextHeader>
        ),
        cell: (info) => (
          <TextCellStyled size={'S'} colorText={'black'}>
            {info.getValue() as string}
          </TextCellStyled>
        ),
      }),
      columnHelper.accessor(getColumnId(translatedColumnNames.pkInit.id), {
        header: sortableHeader('pkInit'),
        cell: (info) => (
          <TextCellStyled size={'S'} colorText={'black'}>
            {`${info.getValue()} km`}
          </TextCellStyled>
        ),
      }),
      columnHelper.accessor(getColumnId(translatedColumnNames.pkEnd.id), {
        header: sortableHeader('pkEnd'),
        cell: (info) => (
          <TextCellStyled size={'S'} colorText={'black'}>
            {`${info.getValue()} km`}
          </TextCellStyled>
        ),
      }),
      columnHelper.accessor(getColumnId(translatedColumnNames.user.id), {
        header: () => (
          <TextHeader size={'XS'} colorText={'greyDark'}>
            {translatedColumnNames.user.label}
          </TextHeader>
        ),
        cell: (info) => (
          <TextCellStyled size={'S'} colorText={'black'}>
            {(info.getValue() as string) || ''}
          </TextCellStyled>
        ),
      }),
      columnHelper.accessor(getColumnId(translatedColumnNames.description.id), {
        header: () => (
          <TextHeader size={'XS'} colorText={'greyDark'}>
            {translatedColumnNames.description.label}
          </TextHeader>
        ),
        cell: (info) => (
          <TextCellStyled size={'S'} colorText={'black'}>
            {info.getValue() as string}
          </TextCellStyled>
        ),
      }),
      columnHelper.accessor(
        getColumnId(translatedColumnNames.creationDate.id),
        {
          header: sortableHeader('creationDate'),
          cell: (info) => (
            <TextCellStyled size={'S'} colorText={'black'}>
              {format(
                new Date(info.getValue() as string),
                'dd/MM/yyyy - HH:mm'
              )}
            </TextCellStyled>
          ),
        }
      ),
    ],
    [
      translatedColumnNames,
      sortableHeader,
      sizeTag,
      showFlagColumn,
      getFlagOrderId,
      t,
    ]
  );

  // Tarea #11 — Sincronizar TanStack pageSize con `takePagination` (server).
  const pagination = useMemo(
    () => ({
      pageIndex,
      pageSize: takePagination,
    }),
    [pageIndex, takePagination]
  );

  const filterFns = {
    roidFilter: roidFilterFunction,
    dateFilter: dateFilterFunction,
  };

  const table = useReactTable({
    getRowId: (row: any) => row.id,
    data: dataAll,
    columns,
    state: {
      rowSelection,
      columnFilters,
      pagination,
      columnVisibility,
    },
    filterFns,
    manualSorting: true,
    onColumnVisibilityChange: setColumnVisibility,
    enableRowSelection: true, //enable row selection for all rows
    onPaginationChange: setPagination,
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnFiltersChange: setColumnFilters,
    getPaginationRowModel: getPaginationRowModel(),
  });

  // Flag to skip sync after restore (prevents infinite loop)
  const isRestoringRef = useRef(false);

  // Update rowState when selection changes (rowSelection uses MongoDB IDs via getRowId)
  useEffect(() => {
    if (isControlled) return;
    if (isRestoringRef.current) {
      isRestoringRef.current = false;
      return;
    }
    const selectedIds = Object.keys(rowSelection).filter((key) => rowSelection[key]);
    setRowState(selectedIds);
  }, [rowSelection, setRowState, isControlled]);

  // Refs for persistent selection state
  const rowStateRef = useRef(rowState);
  useEffect(() => { rowStateRef.current = rowState; }, [rowState]);
  const excludedIdsRef = useRef(excludedIds);
  useEffect(() => { excludedIdsRef.current = excludedIds; }, [excludedIds]);

  // Restore persistent selections when page data changes
  useEffect(() => {
    if (isControlled) return;
    const newSelection: Record<string, boolean> = {};
    if (selectAllMode) {
      dataAll.forEach((row: any) => {
        if (row.id && !excludedIdsRef.current?.has(row.id)) {
          newSelection[row.id] = true;
        }
      });
    } else if (rowStateRef.current && rowStateRef.current.length > 0) {
      const persistedSet = new Set(rowStateRef.current);
      dataAll.forEach((row: any) => {
        if (row.id && persistedSet.has(row.id)) {
          newSelection[row.id] = true;
        }
      });
    }
    isRestoringRef.current = true;
    setRowSelection(newSelection);
  }, [dataAll, selectAllMode, isControlled]);

  const groupInterlocutorOptions =
    data.filtersData?.data?.interlocutorGroups?.map(
      (interlocutor: { name: string; id: string }) => ({
        label: interlocutor.name,
        value: interlocutor.id,
      })
    );

  const concessionOptions = useMemo(
    () =>
      data.filtersData?.data?.concessions?.map((concession) => ({
        label: concession.name,
        value: concession.id,
        roadIDs: concession.roadIDs,
      })),
    [data.filtersData?.data?.concessions]
  );

  const classificationOptions = data.filtersData?.data?.classifications
    ?.slice()
    ?.sort((a, b) => (a.classificationCode ?? 0) - (b.classificationCode ?? 0))
    ?.map((classification) => ({
      label: `${classification.classificationCode} - ${classification.name}`,
      value: classification.id,
    }));

  const predefinedOrderOptions = data.filtersData?.data?.predefinedOrders?.map(
    (predefined: { id: string; name: string }) => ({
      label: predefined.name,
      value: predefined.id,
    })
  );

  // User options for filtering (multi-select). Reusa la lógica de Multimedia:
  // solo usuarios activos con rol {SuperAdmin, Coex, Tablet}. Plan 2 Tarea 16.
  //
  // Plan Task 9: cuando `includeInactive=false` pero el cursor ya tiene
  // userIds seleccionados que NO están en la base activa (porque están
  // inactivos), se mergean como missing options para que los chips
  // sigan renderizando (edge case 7.1 del spec).
  const userOptions = useMemo(() => {
    const base = filterAndSortMultimediaUsers(
      data?.filtersData?.data?.users ?? [],
      selectorCursor?.includeInactive ?? false,
    );
    const selectedIds = selectorCursor?.userIds ?? [];
    const missingIds = selectedIds.filter(
      (id: string) => !base.find((o) => o.value === id),
    );
    if (missingIds.length === 0) return base;
    const allUsers = data?.filtersData?.data?.users ?? [];
    const missingOptions = missingIds
      .map((id: string) => allUsers.find((u: { id: string }) => u.id === id))
      .filter((u: unknown): u is { id?: string; name?: string; surName?: string; isActive?: boolean } => Boolean(u))
      .map((u) => ({
        label:
          u?.isActive === false
            ? `${u?.name ?? ''} ${u?.surName ?? ''} (inactivo)`.trim()
            : `${u?.name ?? ''} ${u?.surName ?? ''}`.trim(),
        value: u?.id ?? '',
      }));
    return [...base, ...missingOptions];
  }, [
    data?.filtersData?.data?.users,
    selectorCursor?.includeInactive,
    selectorCursor?.userIds,
  ]);

  //I select the column and we pass the filter with the input values
  const onChangeInputExpedient = useCallback<onChangeInputExpedientTypes>(
    async (value) => {
      const dataCursor = {
        take: selectorCursor.take,
        cursor: selectorCursor.cursor,
        roadId: selectorData?.roadId ?? selectorCursor?.roadId,
        concessionId:
          selectorData?.concessionId ?? selectorCursor?.concessionId,
        interlocutorGroupId:
          selectorData?.interlocutorGroupId ??
          selectorCursor?.interlocutorGroupId,
        fromDate: selectorData?.fromDate ?? selectorCursor?.fromDate,
        toDate: selectorData?.toDate ?? selectorCursor?.toDate,
        indexType: selectorData.indexType ?? selectorCursor.indexType,
        indexExpedition: value,
        classification:
          selectorData.classification ?? selectorCursor.classification,
        pkInitKm: selectorData.pkInitKm ?? selectorCursor.pkInitKm,
        pkInitMeter: selectorData.pkInitMeter ?? selectorCursor.pkInitMeter,
        pkEndKm: selectorData.pkEndKm ?? selectorCursor.pkEndKm,
        pkEndMeter: selectorData.pkEndMeter ?? selectorCursor.pkEndMeter,
      };
      dispatch(setCursor(tableId, dataCursor));
      setSelectorData((prevData) => {
        const newData = { ...prevData };
        newData.indexExpedition = value;
        return newData;
      });
      setExpedientDefault(value);
    },
    [
      selectorCursor,
      selectorData?.roadId,
      selectorData?.concessionId,
      selectorData?.interlocutorGroupId,
      selectorData?.fromDate,
      selectorData?.toDate,
      selectorData.indexType,
      selectorData.classification,
      selectorData.pkInitKm,
      selectorData.pkEndKm,
    ]
  );

  const onChangeInputIndex = useCallback<onChangeInputIndexTypes>(
    async (value) => {
      const dataCursor = {
        take: selectorCursor.take,
        cursor: selectorCursor.cursor,
        roadId: selectorData?.roadId ?? selectorCursor?.roadId,
        concessionId:
          selectorData?.concessionId ?? selectorCursor?.concessionId,
        interlocutorGroupId:
          selectorData?.interlocutorGroupId ??
          selectorCursor?.interlocutorGroupId,
        fromDate: selectorData?.fromDate ?? selectorCursor?.fromDate,
        toDate: selectorData?.toDate ?? selectorCursor?.toDate,
        indexType: value,
        indexExpedition:
          selectorData.indexExpedition ?? selectorCursor.indexExpedition,
        classification:
          selectorData.classification ?? selectorCursor.classification,
        pkInitKm: selectorData.pkInitKm ?? selectorCursor.pkInitKm,
        pkInitMeter: selectorData.pkInitMeter ?? selectorCursor.pkInitMeter,
        pkEndKm: selectorData.pkEndKm ?? selectorCursor.pkEndKm,
        pkEndMeter: selectorData.pkEndMeter ?? selectorCursor.pkEndMeter,
      };
      dispatch(setCursor(tableId, dataCursor));
      setSelectorData((prevData) => {
        const newData = { ...prevData };
        newData.indexType = value;
        return newData;
      });
      setIndexDefault(value);
    },
    [
      selectorCursor,
      selectorData?.roadId,
      selectorData?.concessionId,
      selectorData?.interlocutorGroupId,
      selectorData?.fromDate,
      selectorData?.toDate,
      selectorData.indexExpedition,
      selectorData.classification,
      selectorData.pkInitKm,
      selectorData.pkEndKm,
    ]
  );

  const onChangePKInit = useCallback<onChangeInputPKTypes>(
    (km, meter) => {
      setSelectorData((prevData) => ({ ...prevData, pkInitKm: km, pkInitMeter: meter }));
    },
    []
  );

  const onChangePKEnd = useCallback<onChangeInputPKTypes>(
    (km, meter) => {
      setSelectorData((prevData) => ({ ...prevData, pkEndKm: km, pkEndMeter: meter }));
    },
    []
  );

  /**
   * *Function for filtering date ranges
   * @param range is a range of dates ({from, to})
   */
  const onChangeDateInput = useCallback<onChangeInputType>(
    (range) => {
      if (range.to) {
        range.to.setHours(23);
        range.to.setMinutes(59);
        range.to.setSeconds(59);
        range.to.setMilliseconds(999);
      }
      const fromDateIso = formatISO(range.from as Date);
      const toDateIso = formatISO(range.to as Date);
      const dataCursor = {
        take: selectorCursor.take,
        cursor: selectorCursor?.cursor,
        indexType: selectorData?.indexType ?? selectorCursor?.indexType,
        indexExpedition:
          selectorData?.indexExpedition ?? selectorCursor?.indexExpedition,
        roadId: selectorData?.roadId ?? selectorCursor?.roadId,
        concessionId:
          selectorData?.concessionId ?? selectorCursor?.concessionId,
        interlocutorGroupId:
          selectorData?.interlocutorGroupId ??
          selectorCursor?.interlocutorGroupId,
        fromDate: fromDateIso,
        toDate: toDateIso,
        classification:
          selectorData?.classification ?? selectorCursor?.classification,
        pkInitKm: selectorData.pkInitKm ?? selectorCursor.pkInitKm,
        pkInitMeter: selectorData.pkInitMeter ?? selectorCursor.pkInitMeter,
        pkEndKm: selectorData.pkEndKm ?? selectorCursor.pkEndKm,
        pkEndMeter: selectorData.pkEndMeter ?? selectorCursor.pkEndMeter,
      };
      dispatch(setCursor(tableId, dataCursor));
      setSelectorData((prevData) => {
        const newData = prevData;
        newData.fromDate = fromDateIso;
        newData.toDate = toDateIso;
        return newData;
      });
    },
    [
      selectorCursor,
      selectorData?.indexType,
      selectorData?.indexExpedition,
      selectorData?.roadId,
      selectorData?.concessionId,
      selectorData?.interlocutorGroupId,
      selectorData?.classification,
      selectorData.pkInitKm,
      selectorData.pkEndKm,
    ]
  );

  const onChangeSelectedPartnerGroup = useCallback(
    (option: option) => {
      const dataCursor = {
        take: selectorCursor.take,
        cursor: selectorCursor?.cursor,
        indexType: selectorData?.indexType ?? selectorCursor?.indexType,
        indexExpedition:
          selectorData?.indexExpedition ?? selectorCursor?.indexExpedition,
        roadId: selectorData?.roadId ?? selectorCursor?.roadId,
        concessionId:
          selectorData?.concessionId ?? selectorCursor?.concessionId,
        interlocutorGroupId: option.value,
        fromDate: selectorData?.fromDate ?? selectorCursor?.fromDate,
        toDate: selectorData?.toDate ?? selectorCursor?.toDate,
        classification:
          selectorData?.classification ?? selectorCursor?.classification,
        pkInitKm: selectorData.pkInitKm ?? selectorCursor.pkInitKm,
        pkInitMeter: selectorData.pkInitMeter ?? selectorCursor.pkInitMeter,
        pkEndKm: selectorData.pkEndKm ?? selectorCursor.pkEndKm,
        pkEndMeter: selectorData.pkEndMeter ?? selectorCursor.pkEndMeter,
      };
      dispatch(setCursor(tableId, dataCursor));
      setSelectorData((prevData) => {
        const newData = prevData;
        newData.interlocutorGroupId = option.value;
        return newData;
      });
    },
    [
      selectorCursor,
      selectorData?.indexType,
      selectorData?.indexExpedition,
      selectorData?.roadId,
      selectorData?.concessionId,
      selectorData?.interlocutorGroupId,
      selectorData?.fromDate,
      selectorData?.toDate,
      selectorData?.classification,
      selectorData.pkInitKm,
      selectorData.pkEndKm,
    ]
  );

  const onChangeSelectedConcession = useCallback(
    async (option: option) => {
      const { data } = await client.query({
        query: GET_ROADS_BY_IDS,
        variables: {
          ids: option.roadIDs,
        },
      });

      setRoadOptions(
        data?.readRoads?.map((road: { id: string; name: string }) => ({
          value: road.id,
          label: road.name,
        }))
      );
      const dataCursor = {
        take: selectorCursor.take,
        cursor: selectorCursor?.cursor,
        indexType: selectorData?.indexType ?? selectorCursor?.indexType,
        indexExpedition:
          selectorData?.indexExpedition ?? selectorCursor?.indexExpedition,
        roadId: undefined,
        concessionId: option.value,
        interlocutorGroupId:
          selectorData?.interlocutorGroupId ??
          selectorCursor?.interlocutorGroupId,
        fromDate: selectorData?.fromDate ?? selectorCursor?.fromDate,
        toDate: selectorData?.toDate ?? selectorCursor?.toDate,
        classification:
          selectorData?.classification ?? selectorCursor?.classification,
        pkInitKm: selectorData.pkInitKm ?? selectorCursor.pkInitKm,
        pkInitMeter: selectorData.pkInitMeter ?? selectorCursor.pkInitMeter,
        pkEndKm: selectorData.pkEndKm ?? selectorCursor.pkEndKm,
        pkEndMeter: selectorData.pkEndMeter ?? selectorCursor.pkEndMeter,
      };
      dispatch(setCursor(tableId, dataCursor));
      setSelectorData((prevData) => {
        const newData = prevData;
        newData.concessionId = option.value;
        newData.roadId = undefined;
        return newData;
      });

      inputRoadRef.current?.clearSelected();
    },
    [
      selectorCursor,
      selectorData?.indexType,
      selectorData?.indexExpedition,
      selectorData?.interlocutorGroupId,
      selectorData?.fromDate,
      selectorData?.toDate,
      selectorData?.classification,
      selectorData.pkInitKm,
      selectorData.pkEndKm,
    ]
  );

  // Auto-select the only concession if there's only one available and filter roads
  useEffect(() => {
    if (
      concessionOptions &&
      concessionOptions.length === 1 &&
      !selectorCursor.concessionId &&
      !hasAutoSelectedConcession.current
    ) {
      const onlyConcession = concessionOptions[0];
      hasAutoSelectedConcession.current = true;
      // Call the callback to filter roads by concession
      onChangeSelectedConcession(onlyConcession);
    }
  }, [concessionOptions, selectorCursor.concessionId, onChangeSelectedConcession]);

  // Filter roads if concessionId is already in cursor (on mount or navigation)
  // Also re-filter when data changes (after revalidate)
  useEffect(() => {
    const filterRoadsByConcessionOnMount = async () => {
      if (
        selectorCursor.concessionId &&
        concessionOptions &&
        concessionOptions.length > 0
      ) {
        const selectedConcession = concessionOptions.find(
          (c) => c.value === selectorCursor.concessionId
        );
        if (selectedConcession && selectedConcession.roadIDs) {
          const { data } = await client.query({
            query: GET_ROADS_BY_IDS,
            variables: {
              ids: selectedConcession.roadIDs,
            },
          });

          const filteredRoads = data?.readRoads?.map(
            (road: { id: string; name: string }) => ({
              value: road.id,
              label: road.name,
            })
          );

          setRoadOptions(filteredRoads || []);
        }
      } else if (!selectorCursor.concessionId && initialRoadOptions) {
        // If no concession is selected, restore all roads
        setRoadOptions(initialRoadOptions);
      }
    };

    filterRoadsByConcessionOnMount();
  }, [selectorCursor.concessionId, concessionOptions, initialRoadOptions]);

  const onChangeSelectedRoad = useCallback(
    (option: option) => {
      const dataCursor = {
        take: selectorCursor.take,
        cursor: selectorCursor?.cursor,
        indexType: selectorData?.indexType ?? selectorCursor?.indexType,
        indexExpedition:
          selectorData?.indexExpedition ?? selectorCursor?.indexExpedition,
        roadId: option.value,
        concessionId:
          selectorData?.concessionId ?? selectorCursor?.concessionId,
        interlocutorGroupId:
          selectorData?.interlocutorGroupId ??
          selectorCursor?.interlocutorGroupId,
        fromDate: selectorData?.fromDate ?? selectorCursor?.fromDate,
        toDate: selectorData?.toDate ?? selectorCursor?.toDate,
        classification:
          selectorData?.classification ?? selectorCursor?.classification,
        pkInitKm: selectorData.pkInitKm ?? selectorCursor.pkInitKm,
        pkInitMeter: selectorData.pkInitMeter ?? selectorCursor.pkInitMeter,
        pkEndKm: selectorData.pkEndKm ?? selectorCursor.pkEndKm,
        pkEndMeter: selectorData.pkEndMeter ?? selectorCursor.pkEndMeter,
      };
      dispatch(setCursor(tableId, dataCursor));
      setSelectorData((prevData) => {
        const newData = prevData;
        newData.roadId = option.value;
        return newData;
      });
    },
    [
      selectorCursor,
      selectorData?.indexType,
      selectorData?.indexExpedition,
      selectorData?.concessionId,
      selectorData?.interlocutorGroupId,
      selectorData?.fromDate,
      selectorData?.toDate,
      selectorData?.classification,
      selectorData.pkInitKm,
      selectorData.pkEndKm,
    ]
  );

  const onChangeSelectedStates = useCallback(
    (values: string[]) => {
      // Canonicalize: sort stable so Apollo cache partitioning by `keyArgs: [..., 'states']`
      // produces the same key for the same selection regardless of click order.
      const sorted = [...values].sort();
      const nextStates = sorted.length > 0 ? sorted : undefined;
      const dataCursor = {
        take: selectorCursor.take,
        cursor: selectorCursor?.cursor,
        indexType: selectorData?.indexType ?? selectorCursor?.indexType,
        indexExpedition:
          selectorData?.indexExpedition ?? selectorCursor?.indexExpedition,
        roadId: selectorData?.roadId ?? selectorCursor?.roadId,
        concessionId:
          selectorData?.concessionId ?? selectorCursor?.concessionId,
        interlocutorGroupId:
          selectorData?.interlocutorGroupId ??
          selectorCursor?.interlocutorGroupId,
        fromDate: selectorData?.fromDate ?? selectorCursor?.fromDate,
        toDate: selectorData?.toDate ?? selectorCursor?.toDate,
        classification:
          selectorData?.classification ?? selectorCursor?.classification,
        states: nextStates,
        userIds: selectorData?.userIds ?? selectorCursor?.userIds,
        includeInactive: selectorData?.includeInactive ?? selectorCursor?.includeInactive,
        pkInitKm: selectorData.pkInitKm ?? selectorCursor.pkInitKm,
        pkInitMeter: selectorData.pkInitMeter ?? selectorCursor.pkInitMeter,
        pkEndKm: selectorData.pkEndKm ?? selectorCursor.pkEndKm,
        pkEndMeter: selectorData.pkEndMeter ?? selectorCursor.pkEndMeter,
      };
      dispatch(setCursor(tableId, dataCursor));
      setSelectorData((prevData) => ({ ...prevData, states: nextStates }));
    },
    [
      selectorCursor,
      selectorData?.indexType,
      selectorData?.indexExpedition,
      selectorData?.roadId,
      selectorData?.concessionId,
      selectorData?.interlocutorGroupId,
      selectorData?.fromDate,
      selectorData?.toDate,
      selectorData?.classification,
      selectorData?.userIds,
      selectorData.pkInitKm,
      selectorData.pkEndKm,
      dispatch,
      tableId,
    ]
  );

  const onChangeSelectedUserIds = useCallback(
    (values: string[]) => {
      // Canonicalize: sort stable so Apollo cache partitioning by
      // `keyArgs: [..., 'userIds']` produces the same key for the same
      // selection regardless of click order.
      const sorted = [...values].sort();
      const nextUserIds = sorted.length > 0 ? sorted : undefined;
      const dataCursor = {
        take: selectorCursor.take,
        cursor: selectorCursor?.cursor,
        indexType: selectorData?.indexType ?? selectorCursor?.indexType,
        indexExpedition:
          selectorData?.indexExpedition ?? selectorCursor?.indexExpedition,
        roadId: selectorData?.roadId ?? selectorCursor?.roadId,
        concessionId:
          selectorData?.concessionId ?? selectorCursor?.concessionId,
        interlocutorGroupId:
          selectorData?.interlocutorGroupId ??
          selectorCursor?.interlocutorGroupId,
        fromDate: selectorData?.fromDate ?? selectorCursor?.fromDate,
        toDate: selectorData?.toDate ?? selectorCursor?.toDate,
        classification:
          selectorData?.classification ?? selectorCursor?.classification,
        states: selectorData?.states ?? selectorCursor?.states,
        userIds: nextUserIds,
        includeInactive: selectorData?.includeInactive ?? selectorCursor?.includeInactive,
        pkInitKm: selectorData.pkInitKm ?? selectorCursor.pkInitKm,
        pkInitMeter: selectorData.pkInitMeter ?? selectorCursor.pkInitMeter,
        pkEndKm: selectorData.pkEndKm ?? selectorCursor.pkEndKm,
        pkEndMeter: selectorData.pkEndMeter ?? selectorCursor.pkEndMeter,
      };
      dispatch(setCursor(tableId, dataCursor));
      setSelectorData((prevData) => ({ ...prevData, userIds: nextUserIds }));
    },
    [
      selectorCursor,
      selectorData?.indexType,
      selectorData?.indexExpedition,
      selectorData?.roadId,
      selectorData?.concessionId,
      selectorData?.interlocutorGroupId,
      selectorData?.fromDate,
      selectorData?.toDate,
      selectorData?.classification,
      selectorData?.states,
      selectorData.pkInitKm,
      selectorData.pkEndKm,
      dispatch,
      tableId,
    ]
  );

  // Plan Task 9: toggle "Incluir inactivos en Creador".
  // Solo expone usuarios desactivados en el dropdown Creador; NO filtra
  // resultados del listado. Persiste en el cursor (Redux) y dispara
  // onReFetchData via useEffect aguas abajo.
  const onChangeIncludeInactive = useCallback(
    (next: boolean) => {
      const dataCursor = {
        take: selectorCursor.take,
        cursor: '',
        indexType: selectorData?.indexType ?? selectorCursor?.indexType,
        indexExpedition:
          selectorData?.indexExpedition ?? selectorCursor?.indexExpedition,
        roadId: selectorData?.roadId ?? selectorCursor?.roadId,
        concessionId:
          selectorData?.concessionId ?? selectorCursor?.concessionId,
        interlocutorGroupId:
          selectorData?.interlocutorGroupId ??
          selectorCursor?.interlocutorGroupId,
        fromDate: selectorData?.fromDate ?? selectorCursor?.fromDate,
        toDate: selectorData?.toDate ?? selectorCursor?.toDate,
        classification:
          selectorData?.classification ?? selectorCursor?.classification,
        states: selectorData?.states ?? selectorCursor?.states,
        userIds: selectorData?.userIds ?? selectorCursor?.userIds,
        includeInactive: next,
        pkInitKm: selectorData.pkInitKm ?? selectorCursor.pkInitKm,
        pkInitMeter: selectorData.pkInitMeter ?? selectorCursor.pkInitMeter,
        pkEndKm: selectorData.pkEndKm ?? selectorCursor.pkEndKm,
        pkEndMeter: selectorData.pkEndMeter ?? selectorCursor.pkEndMeter,
      };
      dispatch(setCursor(tableId, dataCursor));
      setSelectorData((prevData) => ({
        ...prevData,
        includeInactive: next,
      }));
    },
    [selectorCursor, selectorData, dispatch, tableId],
  );

  // «Solo marcadas» (spec 2026-10-22 § 8.3). Solo guarda en el cursor —el
  // reducer hace merge, así que basta con su clave— y la búsqueda la lanza
  // Buscar. Desmarcado es `undefined`, nunca `false`: así la caché de Apollo no
  // se parte en dos claves para «sin filtro». También en `selectorData`, que
  // el selector de tamaño de página mezcla sobre el cursor.
  const onChangeOnlyFlagged = useCallback(
    (next: boolean) => {
      const onlyFlagged: true | undefined = next ? true : undefined;
      dispatch(setCursor(tableId, { onlyFlagged }));
      setSelectorData((prevData) => ({ ...prevData, onlyFlagged }));
    },
    [dispatch, tableId]
  );

  const onChangeSelectedClassification = (value: option) => {
    setSelectorData((prevData) => {
      const newData = { ...prevData };
      newData.classification = value.value;
      return newData;
    });
  };

  const onChangeSelectedPredefinedOrder = useCallback(
    (option: option) => {
      const dataCursor = {
        take: selectorCursor.take,
        cursor: selectorCursor?.cursor,
        indexType: selectorData?.indexType ?? selectorCursor?.indexType,
        indexExpedition:
          selectorData?.indexExpedition ?? selectorCursor?.indexExpedition,
        roadId: selectorData?.roadId ?? selectorCursor?.roadId,
        concessionId:
          selectorData?.concessionId ?? selectorCursor?.concessionId,
        interlocutorGroupId:
          selectorData?.interlocutorGroupId ??
          selectorCursor?.interlocutorGroupId,
        fromDate: selectorData?.fromDate ?? selectorCursor?.fromDate,
        toDate: selectorData?.toDate ?? selectorCursor?.toDate,
        classification:
          selectorData?.classification ?? selectorCursor?.classification,
        predefinedOrderId: option.value,
        states: selectorData?.states ?? selectorCursor?.states,
        userIds: selectorData?.userIds ?? selectorCursor?.userIds,
        includeInactive: selectorData?.includeInactive ?? selectorCursor?.includeInactive,
        pkInitKm: selectorData.pkInitKm ?? selectorCursor.pkInitKm,
        pkInitMeter: selectorData.pkInitMeter ?? selectorCursor.pkInitMeter,
        pkEndKm: selectorData.pkEndKm ?? selectorCursor.pkEndKm,
        pkEndMeter: selectorData.pkEndMeter ?? selectorCursor.pkEndMeter,
      };
      dispatch(setCursor(tableId, dataCursor));
      setSelectorData((prevData) => {
        const newData = prevData;
        newData.predefinedOrderId = option.value;
        return newData;
      });
    },
    [
      selectorCursor,
      selectorData?.indexType,
      selectorData?.indexExpedition,
      selectorData?.roadId,
      selectorData?.concessionId,
      selectorData?.interlocutorGroupId,
      selectorData?.fromDate,
      selectorData?.toDate,
      selectorData?.classification,
      selectorData?.states,
      selectorData?.userIds,
      selectorData.pkInitKm,
      selectorData.pkEndKm,
    ]
  );

  const onClearValue = useCallback(() => {
    inputListGroupRef.current?.clearSelected();
    inputDateRef.current?.clearSelected();
    inputExpedientRef.current?.clearSelected();
    inputIndexRef.current?.clearSelected();
    inputIndexRef.current?.clearSelected();
    inputPKInitRef.current?.clearSelected();
    inputPKEndRef.current?.clearSelected();
    inputRoadRef.current?.clearSelected();
    inputListClassificationRef.current?.clearSelected();
    inputStateRef.current?.clearSelected();
    inputUserRef.current?.clearSelected();
    inputListPredefinedRef.current?.clearSelected();
    setColumnFilters([]);

    // Check if there's only one concession
    const hasOnlyOneConcession = concessionOptions && concessionOptions.length === 1;
    const onlyConcessionId = hasOnlyOneConcession ? concessionOptions[0].value : undefined;

    // If there's only one concession, keep it selected
    // The useEffect will handle re-filtering roads automatically when concessionId changes
    if (!hasOnlyOneConcession) {
      inputListConRef.current?.clearSelected();
      hasAutoSelectedConcession.current = false;
    }

    const dataCursor = {
      take: takePagination,
      cursor: '',
      indexType: undefined,
      indexExpedition: undefined,
      roadId: undefined,
      concessionId: hasOnlyOneConcession ? onlyConcessionId : undefined,
      interlocutorGroupId: undefined,
      fromDate: undefined,
      toDate: undefined,
      classification: undefined,
      predefinedOrderId: undefined,
      state: undefined,
      states: undefined,
      userIds: undefined,
      includeInactive: undefined,
      pkInitKm: undefined,
      pkInitMeter: undefined,
      pkEndKm: undefined,
      pkEndMeter: undefined,
      description: undefined,
      orderBy: 'desc',
      sortField: undefined,
      // Explícito: el reducer hace merge y omitirlo conservaría el filtro.
      onlyFlagged: undefined,
    };
    dispatch(setCursor(tableId, dataCursor));
    // Fix: Create new object instead of mutating to trigger useEffect
    setSelectorCursor((prevData) => ({
      ...prevData,
      indexType: undefined,
      indexExpedition: undefined,
      roadId: undefined,
      interlocutorGroupId: undefined,
      concessionId: hasOnlyOneConcession ? onlyConcessionId : undefined,
      fromDate: undefined,
      toDate: undefined,
      classification: undefined,
      predefinedOrderId: undefined,
      state: undefined,
      states: undefined,
      userIds: undefined,
      includeInactive: undefined,
      pkInitKm: undefined,
      pkInitMeter: undefined,
      pkEndKm: undefined,
      pkEndMeter: undefined,
      description: undefined,
      sortField: undefined,
      orderBy: undefined,
      onlyFlagged: undefined,
    }));
    setExpedientDefault(undefined);
    setIndexDefault(undefined);
    setSelectorData((prevData) => ({
      ...prevData,
      indexType: undefined,
      indexExpedition: undefined,
      roadId: undefined,
      interlocutorGroupId: undefined,
      concessionId: hasOnlyOneConcession ? onlyConcessionId : undefined,
      fromDate: undefined,
      toDate: undefined,
      classification: undefined,
      predefinedOrderId: undefined,
      state: undefined,
      states: undefined,
      userIds: undefined,
      includeInactive: undefined,
      pkInitKm: undefined,
      pkInitMeter: undefined,
      pkEndKm: undefined,
      pkEndMeter: undefined,
      description: undefined,
      onlyFlagged: undefined,
    }));
    setCurrentServerPage(1);
    onReFetchData?.();
    // Tarea #11 fix: `takePagination` debe estar en deps para evitar stale closure.
  }, [concessionOptions, initialRoadOptions, onChangeInputExpedient, onChangeInputIndex, takePagination]);

  const handleClickRow = useCallback(
    (row: Row<dataTable>) => {
      const val: boolean = row.getIsSelected();
      const newRowState = [...rowState];
      if (!val) {
        newRowState.push(row.original.id);
        setRowState(newRowState);
      } else {
        const index = newRowState.indexOf(row.original.id);
        if (index !== -1) {
          newRowState.splice(index, 1);
        }
        setRowState(newRowState);
      }
    },
    [rowState]
  );

  // Columnas que ofrece el desplegable «Columnas»: todas menos la de selección
  // (la primera) y las que no se pueden ocultar (la bandera, § 8.2). Sin
  // bandera queda igual que el `.slice(1)` de siempre.
  const hideableColumns = table
    .getAllColumns()
    .slice(1)
    .filter((column) => column.getCanHide());

  const hiddenColumns: number = Object.values(
    columnVisibility as object
  ).reduce((count, currentValue) => (!currentValue ? count + 1 : count), 0);

  const commonColumns = Object.values(columnNames).filter((column) =>
    table
      .getAllColumns()
      .map((column) => column.id)
      .includes(column.id)
  );

  // commonLabels definition removed

  const fetchCommunicationID = useCallback(
    async (id: string, take = takePagination) => {
      const dataSelector = {
        take,
        cursor: id,
        roadId: selectorData?.roadId ?? selectorCursor?.roadId,
        indexType: selectorData?.indexType ?? selectorCursor?.indexType,
        indexExpedition:
          selectorData?.indexExpedition ?? selectorCursor?.indexExpedition,
        concessionId:
          selectorData?.concessionId ?? selectorCursor?.concessionId,
        interlocutorGroupId:
          selectorData?.interlocutorGroupId ??
          selectorCursor?.interlocutorGroupId,
        fromDate: selectorData?.fromDate ?? selectorCursor?.fromDate,
        toDate: selectorData?.toDate ?? selectorCursor?.toDate,
        sortField: selectorCursor?.sortField,
        orderBy: selectorCursor?.orderBy ?? 'desc',
        classification:
          selectorData?.classification ?? selectorCursor?.classification,
        predefinedOrderId:
          selectorData?.predefinedOrderId ?? selectorCursor?.predefinedOrderId,
        pkInitKm: selectorData.pkInitKm ?? selectorCursor.pkInitKm,
        pkInitMeter: selectorData.pkInitMeter ?? selectorCursor.pkInitMeter,
        pkEndKm: selectorData.pkEndKm ?? selectorCursor.pkEndKm,
        pkEndMeter: selectorData.pkEndMeter ?? selectorCursor.pkEndMeter,
      };

      if (take > 0) {
        const lastCommunication = dataAll[dataAll.length - 1].communicationID;
        if (lastCommunication) {
          dispatch(setCursor(tableId, dataSelector));
        }
      } else {
        const firstCommunication = dataAll[0].communicationID;

        if (firstCommunication) {
          dispatch(setCursor(tableId, dataSelector));
        }
      }

      return dataSelector;
    },
    [
      selectorCursor,
      selectorData?.indexType,
      selectorData?.indexExpedition,
      selectorData?.roadId,
      selectorData?.concessionId,
      selectorData?.interlocutorGroupId,
      selectorData?.fromDate,
      selectorData?.toDate,
      selectorData?.classification,
      selectorData?.predefinedOrderId,
      selectorData.pkInitKm,
      selectorData.pkEndKm,
      dataAll,
      takePagination,
    ]
  );

  const nextPagination = async () => {
    if (!isControlled) setRowSelection({});
    // Clamp defensivo contra bug off-by-one (backend/cache hasNextPage true en última real).
    const pageCount = Math.max(
      1,
      Math.ceil((data?.totalCount ?? 0) / takePagination),
    );
    if (
      dataAll.length >= 1 &&
      data?.pageInfo.hasNextPage &&
      currentServerPage < pageCount
    ) {
      setCurrentServerPage((prev) => Math.min(prev + 1, pageCount));
      await fetchCommunicationID(
        dataAll[dataAll.length - 1].communicationID as string
      );

      onReFetchPagination();
    }
  };

  const backPagination = async () => {
    if (!isControlled) setRowSelection({});
    if (dataAll.length >= 1 && data?.pageInfo.hasPreviousPage) {
      setCurrentServerPage((prev) => Math.max(1, prev - 1));
      await fetchCommunicationID(
        dataAll[0].communicationID as string,
        -takePagination
      );

      onReFetchPagination();
    }
  };

  const handleSearch = async () => {
    const dataCursor = {
      take: takePagination,
      cursor: '',
      indexType: selectorData?.indexType ?? selectorCursor?.indexType,
      indexExpedition:
        selectorData?.indexExpedition ?? selectorCursor?.indexExpedition,
      roadId: selectorData?.roadId ?? selectorCursor?.roadId,
      concessionId: selectorData?.concessionId ?? selectorCursor?.concessionId,
      interlocutorGroupId:
        selectorData?.interlocutorGroupId ??
        selectorCursor?.interlocutorGroupId,
      fromDate: selectorData?.fromDate ?? selectorCursor?.fromDate,
      toDate: selectorData?.toDate ?? selectorCursor?.toDate,
      classification:
        selectorData?.classification ?? selectorCursor?.classification,
      predefinedOrderId:
        selectorData?.predefinedOrderId ?? selectorCursor?.predefinedOrderId,
      pkInitKm: selectorData.pkInitKm ?? selectorCursor.pkInitKm,
      pkInitMeter: selectorData.pkInitMeter ?? selectorCursor.pkInitMeter,
      pkEndKm: selectorData.pkEndKm ?? selectorCursor.pkEndKm,
      pkEndMeter: selectorData.pkEndMeter ?? selectorCursor.pkEndMeter,
      description:
        (selectorData.description ?? selectorCursor.description) || undefined,
    };
    dispatch(setCursor(tableId, dataCursor));
    setCurrentServerPage(1);
    onReFetchData?.();
  };

  const filterContainerRef = useRef<HTMLDivElement>(null);
  useFilterKeyboardShortcuts(filterContainerRef, handleSearch, onClearValue, isLoadingData as boolean);

  return (
    <ContainerStyled>
      {isLoadingData && (
        <ContainerLoader>
          <Loader />
        </ContainerLoader>
      )}
      <FiltersWithLoadingStyled isLoading={isLoadingData}>
        <SubContainerHead>
          <Title component={'h2'} variant={'semiBold'}>
            {titleText}
          </Title>
          {isLandscapeTablet ? (
            <ShowFiltersButton isBlack={showFilters} onClick={handleClick}>
              <ArrowBottomStyled isRotate={showFilters}></ArrowBottomStyled>
            </ShowFiltersButton>
          ) : null}
        </SubContainerHead>
        {showFilters ? (
          <SubContainerFilters ref={filterContainerRef}>
            <InputExpedient
              label={t('headers.expedient')}
              disabled={isLoadingData}
              expedient={expedientDefault}
              onChangeInputExpedient={onChangeInputExpedient}
              ref={inputExpedientRef}
            />

            <InputIndex
              label={t('headers.index')}
              disabled={isLoadingData}
              index={indexDefault}
              onChangeInputIndex={onChangeInputIndex}
              ref={inputIndexRef}
            />

            <InputPK
              label={t('tables:headers.pkInit')}
              disabled={isLoadingData}
              onChangeInputPK={onChangePKInit}
              ref={inputPKInitRef}
            />

            <InputPK
              label={t('tables:headers.pkEnd')}
              disabled={isLoadingData}
              onChangeInputPK={onChangePKEnd}
              ref={inputPKEndRef}
            />
            {/* Filtro por descripción: texto libre sobre Order.description.
                Busca por subcadena, sin distinguir acentos ni mayúsculas, y
                exige todas las palabras en cualquier orden (el backend filtra
                contra la copia normalizada `descriptionSearch`, indexada). */}
            <InputText
              label={t('tables:headers.description')}
              typeInput="normal"
              inputSize="S"
              placeholder={t('common:placeholders.writeHere')}
              disabled={isLoadingData}
              value={
                selectorData?.description ?? selectorCursor?.description ?? ''
              }
              onChange={(e) =>
                setSelectorData((prev) => ({
                  ...prev,
                  description: e.target.value,
                }))
              }
            />
            <InputCalendarStyled
              ref={inputDateRef}
              label={t('tables:filters.dateRangeFilter')}
              name={'data'}
              dateRange={
                selectorCursor?.fromDate && selectorCursor?.toDate
                  ? {
                    from: parseISO(selectorCursor?.fromDate),
                    to: parseISO(selectorCursor?.toDate),
                  }
                  : undefined
              }
              onChangeInput={onChangeDateInput}
              disabled={isLoadingData}
            />
            {concessionOptions && concessionOptions.length > 1 && (
              <DropDownInput
                inputSize="XS"
                ref={inputListConRef}
                labelText={t('tables:headers.concession')}
                placeholder={t('common:placeholders.select')}
                typeDropDown={'Secondary'}
                defaultValue={selectorCursor?.concessionId ?? ''}
                options={concessionOptions as Options}
                onChangeSelected={onChangeSelectedConcession as onChangeValue}
                isDisabled={isLoadingData}
              />
            )}
            <DropDownInput
              inputSize="XS"
              ref={inputRoadRef}
              labelText={t('tables:headers.road')}
              placeholder={t('common:placeholders.select')}
              typeDropDown={'Secondary'}
              defaultValue={selectorCursor?.roadId ?? ''}
              options={roadOptions as Options}
              onChangeSelected={onChangeSelectedRoad as onChangeValue}
              isDisabled={isLoadingData}
            />

            <SearchableDropdownStyled
              inputSize="L"
              ref={inputListClassificationRef}
              labelText={t('tables:headers.classification')}
              placeholder={t('common:placeholders.select')}
              typeDropDown={'Default'}
              defaultValue={selectorCursor?.classification ?? ''}
              options={classificationOptions as Options}
              onChangeSelected={
                onChangeSelectedClassification as onChangeValue
              }
              isDisabled={isLoadingData}
            />
            {showPredefinedFilter && predefinedOrderOptions && predefinedOrderOptions.length > 0 && (
              <SearchableDropdownStyled
                inputSize="L"
                ref={inputListPredefinedRef}
                labelText={t('forms:workOrder.predefined')}
                placeholder={t('common:placeholders.select')}
                typeDropDown={'Default'}
                defaultValue={selectorCursor?.predefinedOrderId ?? ''}
                options={predefinedOrderOptions as Options}
                onChangeSelected={
                  onChangeSelectedPredefinedOrder as onChangeValue
                }
                isDisabled={isLoadingData}
              />
            )}
            <DropDownInput
              inputSize="M"
              ref={inputListGroupRef}
              labelText={t('tables:headers.interlocutorGroup')}
              placeholder={t('common:placeholders.select')}
              typeDropDown={'Default'}
              defaultValue={selectorCursor?.interlocutorGroupId ?? ''}
              options={groupInterlocutorOptions as Options}
              onChangeSelected={onChangeSelectedPartnerGroup as onChangeValue}
              isDisabled={isLoadingData}
            />
            <MultiSelectDropdownStyled
              inputSize="S"
              ref={inputStateRef}
              labelText={t('tables:headers.status')}
              placeholder={t('common:placeholders.select')}
              typeDropDown={'Secondary'}
              selectedValues={selectorCursor?.states ?? []}
              options={roleActive ? getStateOptionsByRoleAndEntityType(t, roleActive as Role, TABLE_ENTITY_TYPES[tableId as string], listingFiltersByRole(tableId)) : []}
              onChangeSelected={onChangeSelectedStates}
              isDisabled={isLoadingData}
            />
            {userOptions && userOptions.length > 0 && (
              <MultiSelectDropdownStyled
                inputSize="L"
                ref={inputUserRef}
                labelText={t('common:labels.creator')}
                placeholder={t('common:placeholders.select')}
                typeDropDown={'Default'}
                searchable
                selectedValues={selectorCursor?.userIds ?? []}
                options={userOptions}
                onChangeSelected={onChangeSelectedUserIds}
                isDisabled={isLoadingData}
              />
            )}
            <ContainerButtons>
              <ButtonIconBackground
                variant={'delete'}
                onClick={onClearValue}
                disabled={isLoadingData}
                title={`${t('tables:filters.clearFilters')} (Shift+Enter)`}
              />
              <DropDownVisibilityColumn
                columns={hideableColumns}
                columnNames={Object.fromEntries(hideableColumns.map(c => [c.id, translatedColumnNames[c.id as keyof typeof translatedColumnNames]?.label || c.id]))}
                isDisabled={isLoadingData}
              />
              <ButtonIconBackground
                variant={'restart'}
                onClick={() => { setCurrentServerPage(1); onReFetchData?.(); }}
                disabled={isLoadingData}
              />
              <ButtonIconBackground
                variant={'search'}
                onClick={handleSearch}
                disabled={isLoadingData}
                title={`${t('buttons:search')} (Enter)`}
              />
            </ContainerButtons>
            <IncludeInactiveToggle
              checked={selectorCursor?.includeInactive ?? false}
              onChange={onChangeIncludeInactive}
              label={t('common:filters.includeInactiveInCreator')}
              isDisabled={isLoadingData}
            />
            {showOnlyFlaggedFilter && (
              <IncludeInactiveToggle
                checked={Boolean(selectorCursor?.onlyFlagged)}
                onChange={onChangeOnlyFlagged}
                label={flagScopeTexts.onlyFlagged}
                isDisabled={isLoadingData}
              />
            )}
          </SubContainerFilters>
        ) : null}
      </FiltersWithLoadingStyled>
      <TableContainerStyled isLoading={isLoadingData}>
        <Table {...props} id={idCss}>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <Th
                    key={header.id}
                    {...exportIgnoreProps(header.column.id)}
                    aria-sort={
                      activeSortColumnId === header.id
                        ? sortDirection === 'asc'
                          ? 'ascending'
                          : 'descending'
                        : undefined
                    }
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                  </Th>
                ))}
              </tr>
            ))}
          </thead>
          <Tbody>
            {table.getRowModel().rows.map((row) => (
              <DoubleTapRow
                key={row.id}
                StyledComponent={TrStyled}
                componentProps={{
                  checked: row.getIsSelected(),
                }}
                isDisabled={isLoadingData as boolean}
                onSingleTap={() => {
                  row.getToggleSelectedHandler()(row);
                }}
                onDoubleTap={() => {
                  if (row.original?.id) {
                    const path = getDoubleTapNavigationPath
                      ? getDoubleTapNavigationPath(row.original.id)
                      : `/work-orders/communications/${row.original.id}`;
                    navigate(path);
                  }
                }}
              >
                {row.getVisibleCells().map((cell) => (
                  <Td
                    column={cell.column.id as columnID}
                    isFigure={
                      cell.column.id === translatedColumnNames.sendStatus.id
                    }
                    hiddenColumns={hiddenColumns}
                    key={cell.id}
                    {...exportIgnoreProps(cell.column.id)}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </Td>
                ))}
              </DoubleTapRow>
            ))}
          </Tbody>
        </Table>
      </TableContainerStyled>
      {onSelectAll && onClearSelection && (
        <SelectionBanner
          selectedCount={
            isControlled
              ? (propsSelectedCount ?? 0)
              : selectAllMode
                ? (data?.totalCount || 0) - (excludedIds?.size || 0)
                : Object.keys(rowSelection).filter((k) => (rowSelection as Record<string, boolean>)[k]).length
          }
          totalCount={data?.totalCount || 0}
          pageCount={dataAll.length}
          allPageSelected={
            dataAll.length > 0 &&
            dataAll.every((row: any) => (rowSelection as Record<string, boolean>)[row.id])
          }
          selectAllMode={selectAllMode}
          onSelectAll={onSelectAll}
          onClearSelection={onClearSelection}
        />
      )}
      <PaginationButtons
        pageCount={Math.max(1, Math.ceil((data?.totalCount ?? 0) / takePagination))}
        currentPage={currentServerPage}
        totalCount={data?.totalCount ?? 0}
        nextPage={nextPagination}
        previousPage={backPagination}
        isDisabled={isLoadingData}
        // Tarea #11: gates aplicados en `useServerPageSize`. En tablet o
        // cuando la tabla se usa con tableId no-principal, props undefined.
        {...paginationProps}
      />
    </ContainerStyled>
  );
};
